import React from 'react';
import {
  N0,
  N100,
  N40A,
  Green500,
  Red300,
  Red500,
  Yellow500,
} from '@trello/colors';
import cx from 'classnames';
import styles from './Badge.less';

import { IconBadge } from './Badge';
import ArchivedIcon from '../icons/ArchivedIcon';
import AttachmentIcon from '../icons/AttachmentIcon';
import ChecklistIconComplete from '../icons/ChecklistIconComplete';
import ChecklistIconIncomplete from '../icons/ChecklistIconIncomplete';
import CommentIcon from '../icons/CommentIcon';
import DescriptionIcon from '../icons/DescriptionIcon';
import DueDateIconComplete from '../icons/DuedateCompleteIcon';
import DueDateIcon from '../icons/DuedateIcon';
import NotificationIcon from '../icons/NotificationIcon';
import SubscribeIcon from '../icons/SubscribeIcon';
import CardTemplateIcon from '../icons/CardTemplateIcon';
import TrelloAttachmentIcon from '../icons/TrelloAttachmentIcon';
import VoteIcon from '../icons/VoteIcon';

const TWENTY_FOUR_HOURS = 86400000;
const THIRTY_SIX_HOURS = 129600000;

/**
 * Archived Badge
 */
interface ArchivedBadgeProps {
  className?: string;
}

export const ArchivedBadge: React.FC<ArchivedBadgeProps> = ({
  className,
  children,
}) => (
  <IconBadge className={className} icon={<ArchivedIcon />}>
    {children}
  </IconBadge>
);

/**
 * Attachments Badge
 */
interface AttachmentsBadgeProps {
  className?: string;
  numAttachments?: number;
}

export const AttachmentsBadge: React.FC<AttachmentsBadgeProps> = ({
  numAttachments = 0,
  className,
}) => (
  <IconBadge className={className} icon={<AttachmentIcon />}>
    {numAttachments}
  </IconBadge>
);

/**
 * Card Template Badge
 */

interface CardTemplateBadgeProps {
  className?: string;
}

export const CardTemplateBadge: React.FC<CardTemplateBadgeProps> = ({
  className,
  children,
}) => (
  <IconBadge
    className={cx(className, styles.isTemplate)}
    icon={<CardTemplateIcon />}
  >
    {children}
  </IconBadge>
);

/**
 * Checklist Badge
 */
interface ChecklistBadgeProps {
  className?: string;
  numItems?: number;
  numComplete?: number;
}

export const ChecklistBadge: React.FC<ChecklistBadgeProps> = ({
  numComplete = 0,
  numItems = 0,
  className,
}) => {
  const isComplete = numComplete === numItems;

  const bgColor = isComplete ? Green500 : undefined;
  const fontColor = isComplete ? N0 : undefined;
  const icon = isComplete ? (
    <ChecklistIconComplete />
  ) : (
    <ChecklistIconIncomplete />
  );

  return (
    <IconBadge
      className={className}
      bgColor={bgColor}
      fontColor={fontColor}
      icon={icon}
    >
      {numComplete}/{numItems}
    </IconBadge>
  );
};

/**
 * Comments Badge
 */
interface CommentsBadgeProps {
  className?: string;
  numComments?: number;
}

export const CommentsBadge: React.FC<CommentsBadgeProps> = ({
  numComments = 0,
  className,
}) => (
  <IconBadge className={className} icon={<CommentIcon />}>
    {numComments}
  </IconBadge>
);

/**
 * Description Badge
 */
interface DescriptionBadgeProps {
  className?: string;
}

export const DescriptionBadge: React.FC<DescriptionBadgeProps> = ({
  className,
}) => <IconBadge className={className} icon={<DescriptionIcon />} />;

/**
 * DueDate Badge
 */
interface DueDateBadgeProps {
  className?: string;
  dueDate: Date;
  isComplete?: boolean;
}

export const DueDateBadge: React.FC<DueDateBadgeProps> = ({
  children,
  className,
  dueDate,
  isComplete = false,
}) => {
  const now = Date.now();
  const dueDateTime = dueDate.getTime();
  const isDueSoon = dueDateTime - TWENTY_FOUR_HOURS <= now && dueDateTime > now;
  const isDueNow = dueDateTime <= now && dueDateTime + THIRTY_SIX_HOURS > now;
  const isPastDue = dueDateTime + THIRTY_SIX_HOURS <= now;

  let bgColor = 'transparent';
  let fontColor = 'N400';
  let icon = <DueDateIcon />;

  if (!isComplete && isPastDue) {
    bgColor = Red300;
    fontColor = N0;
    icon = <DueDateIconComplete />;
  } else if (!isComplete && isDueNow) {
    bgColor = Red500;
    fontColor = N0;
    icon = <DueDateIconComplete />;
  } else if (!isComplete && isDueSoon) {
    bgColor = Yellow500;
    fontColor = N0;
    icon = <DueDateIconComplete />;
  } else if (isComplete) {
    bgColor = Green500;
    fontColor = N0;
    icon = <DueDateIconComplete />;
  }

  return (
    <IconBadge
      className={className}
      bgColor={bgColor}
      fontColor={fontColor}
      icon={icon}
    >
      {children}
    </IconBadge>
  );
};

/**
 * Notification Badge
 */
interface NotificationBadgeProps {
  className?: string;
  numUnread?: number;
}

export const NotificationBadge: React.FC<NotificationBadgeProps> = ({
  numUnread = 0,
  className,
}) => (
  <IconBadge
    className={className}
    bgColor={Red500}
    fontColor={N0}
    icon={<NotificationIcon />}
  >
    {numUnread}
  </IconBadge>
);

/**
 * Subscribed Badge
 */
interface SubscribedBadgeProps {
  className?: string;
}

export const SubscribedBadge: React.FC<SubscribedBadgeProps> = ({
  className,
}) => <IconBadge className={className} icon={<SubscribeIcon />} />;

/**
 * Trello Attachments Badge
 */
interface TrelloAttachmentsBadgeProps {
  className?: string;
  numAttachments?: number;
}

export const TrelloAttachmentsBadge: React.FC<TrelloAttachmentsBadgeProps> = ({
  numAttachments = 0,
  className,
}) => (
  <IconBadge className={className} icon={<TrelloAttachmentIcon />}>
    {numAttachments}
  </IconBadge>
);

/**
 * Votes Badge
 */
interface VotesBadgeProps {
  className?: string;
  numVotes?: number;
  voted?: boolean;
}

export const VotesBadge: React.FC<VotesBadgeProps> = ({
  className,
  numVotes = 0,
  voted = false,
}) => (
  <IconBadge
    className={className}
    bgColor={voted ? N40A : 'transparent'}
    fontColor={N100}
    icon={<VoteIcon />}
  >
    {numVotes}
  </IconBadge>
);
